import React, { useState, useEffect, useCallback, useMemo } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import {
  IoMdArrowRoundBack,
  IoMdHome,
  IoMdAdd,
  IoMdCheckmarkCircle,
} from "react-icons/io";
import { Loader2 } from "lucide-react";
import StepIndicator from "./StepIndicator";
import WizardStep from "./WizardStep";
import PreviewStep from "./PreviewStep";
import NavigationButtons from "./NavigationButtons";
import ContactInfoStep from "./ContactInfoStep";
import PropertyPriceCalculator from "./PropertyPriceCalculator";
import KruozAPI from "../../KruozAPI";
import OrderSummery from "./OrderSummery";
import EmailQuotation from "./EmailQuotation";
import ShopStep from "./ShopStep";

const SubmissionStatus = ({
  isSubmitting,
  isStepSubmitting,
  isSuccess,
  onNavigateHome,
  onCreateNew,
}) => {
  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
        <h2 className="text-xl font-semibold text-gray-800">
          Submitting Your Quote/Order Request
        </h2>
        <p className="text-gray-600 text-center">
          Please wait while we process your request. This may take a moment...
        </p>
        <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 mt-4">
          <div className="bg-blue-500 h-2.5 rounded-full animate-pulse w-full"></div>
        </div>
      </div>
    );
  }

  if (isStepSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
        <h2 className="text-xl font-semibold text-gray-800">
          Saving step data...
        </h2>
        <p className="text-gray-600 text-center">
          Please wait while we save your progress...
        </p>
        <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 mt-4">
          <div className="bg-blue-500 h-2.5 rounded-full animate-pulse w-full"></div>
        </div>
      </div>
    );
  }
  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-6 relative">
        <div className="absolute top-4 right-4">
          <button
            onClick={onCreateNew}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <IoMdAdd size={20} />
            <span>Create New Request</span>
          </button>
        </div>
        <div className="text-green-500">
          <IoMdCheckmarkCircle size={64} />
        </div>
        <h2 className="text-2xl font-semibold text-gray-800">
          Quote/Order Request Submitted Successfully!
        </h2>
        <p className="text-gray-600 text-center max-w-md">
          Your quotation request has been submitted. You will receive a
          confirmation email shortly.
        </p>
        <div className="mt-8">
          <button
            onClick={onNavigateHome}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <IoMdHome size={20} />
            <span>Open Submitted Request</span>
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const RequestWizard = ({
  company,
  properties,
  user,
  login,
  existingRequest,
}) => {
  const navigate = useNavigate();
  const [currentStepId, setCurrentStepId] = useState(
    company.quotationTemplate.wizardSteps[0]?.id || ""
  );
  const [quotationTemplate, setQuotationTemplate] = useState(
    company.quotationTemplate
  );
  const [errors, setErrors] = useState({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStepSubmitting, setIsStepSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [stepSubmissionStatus, setStepSubmissionStatus] = useState({});
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const stepOrder = useMemo(() => {
    const steps = [
      ...quotationTemplate.wizardSteps,
      { id: "preview", label: "Preview & Submit" },
    ];
    return steps.map((step) => step.id);
  }, [quotationTemplate.wizardSteps]);

  const currentStepIndex = stepOrder.indexOf(currentStepId);

  const [request, setRequest] = useState({
    quotationCompanyId: company.id,
    quotationTemplateId: company.quotationTemplateId,
    quotationCompanyCode: company.code,
    quotationCompanyName: company.name,
    quotationCompanyAddress: company.address,
    quotationCompanyCallPhone: company.callPhone,
    quotationCompanyEmail: company.email,
    quotationCompanyWebsite: company.website,
    quotationCompanyLogo: company.logo,
    quotationCompanyTimeZone: company.timeZone,
    quotationCompanyContactPerson: company.contactPerson,
    quotationCompanyBusiness: company.business,
    customerUserId: user ? user.id : "",
    content: "",
    status: "Draft",
    companyId: "Kruoz",
    createdBy: user ? user.id : "",
  });
  // Load existing request data including selected assets
  useEffect(() => {
    if (existingRequest) {
      const parsedContent =
        typeof existingRequest.content === "string"
          ? JSON.parse(existingRequest.content)
          : existingRequest.content;

      setRequest({
        ...request,
        ...existingRequest,
        content:
          typeof existingRequest.content === "string"
            ? existingRequest.content
            : JSON.stringify(existingRequest.content),
      }); // Set selected assets from the parsed content - only from ShopStep
      let assetsFound = false;

      // Only load selected assets from the ShopStep
      if (parsedContent?.wizardSteps) {
        const shopStep = parsedContent.wizardSteps.find(
          (step) => step.id === "ShopStep"
        );
        if (
          shopStep &&
          shopStep.selectedAssets &&
          shopStep.selectedAssets.length > 0
        ) {
          setSelectedAssets(shopStep.selectedAssets);
          assetsFound = true;
        }
      }

      // Fallback to orderSummary if needed (for backward compatibility)
      if (
        !assetsFound &&
        parsedContent?.orderSummary?.selectedAssets &&
        parsedContent.orderSummary.selectedAssets.length > 0
      ) {
        setSelectedAssets(parsedContent.orderSummary.selectedAssets);
        assetsFound = true;
      }

      // Debug log for troubleshooting
      console.log(
        "Loading selected assets:",
        assetsFound ? "Assets found" : "No assets found",
        assetsFound ? selectedAssets.length : 0
      );

      if (parsedContent?.step) {
        setCurrentStepId(parsedContent.step);
      }

      if (parsedContent?.wizardSteps) {
        setQuotationTemplate((prevTemplate) => ({
          ...prevTemplate,
          wizardSteps: parsedContent.wizardSteps,
        }));
      }
    }
  }, [existingRequest]);

  // useEffect(() => {
  //   debugger;
  //   fetchAvailableAssets();
  // }, [quotationTemplate, company.id, selectedAssets]);

  const fetchAvailableAssets = async () => {
    if (!quotationTemplate?.wizardSteps) return;

    const hireDurationStep = quotationTemplate.wizardSteps.find(
      (step) => step.id === "hireDuration"
    );
    const deliveryDate = hireDurationStep?.fields.find(
      (field) => field.id === "deliveryDate"
    )?.value;
    const collectionDate = hireDurationStep?.fields.find(
      (field) => field.id === "collectionDate"
    )?.value;

    if (!deliveryDate || !collectionDate) return;

    const propertyIds = new Set();
    quotationTemplate.wizardSteps.forEach((step) => {
      step.fields.forEach((field) => {
        if (field.value && field.options) {
          const selectedOption = field.options.find(
            (option) =>
              option.label === field.value || option.value === field.value
          );
          if (selectedOption?.properties) {
            selectedOption.properties.forEach((propId) =>
              propertyIds.add(propId.id)
            );
          }
        }
      });
    });

    if (propertyIds.size === 0) return;

    setIsCheckingAvailability(true);
    try {
      const response = await KruozAPI.get(
        "Assets/AssetForBookingByDateRangeAndProperties",
        {
          params: {
            startDate: deliveryDate,
            endDate: collectionDate,
            properties: Array.from(propertyIds).join(","),
            companyId: company.id,
          },
        }
      );

      // Filter out already selected assets
      const availableAssets = response.data.filter(
        (asset) =>
          asset.IsAvailable &&
          !selectedAssets.some((selected) => selected.Id === asset.Id)
      );

      setAvailableAssets(availableAssets);
    } catch (error) {
      console.error("Error fetching assets:", error);
    } finally {
      setIsCheckingAvailability(false);
      setHasAttemptedFetch(true);
    }
  };

  const calculateCollectionDate = (deliveryDate, rentalDuration) => {
    const date = new Date(deliveryDate);
    date.setDate(date.getDate() + parseInt(rentalDuration));
    // switch (rentalDuration) {
    //   case "Days":
    //     date.setDate(date.getDate() + parseInt(rentalDuration));
    //     break;
    //   case "Weeks":
    //     date.setDate(date.getDate() + parseInt(rentalDuration) * 7);
    //     break;
    //   case "Months":
    //     date.setMonth(date.getMonth() + parseInt(rentalLength));
    //     break;
    //   default:
    //     return null;
    // }
    return date.toISOString().split("T")[0];
  };

  const createOrderSummary = (assets) => {
    return {
      selectedAssets: assets.map((asset) => ({
        Id: asset.Id,
        Code: asset.Code,
        Name: asset.Name,
        AssetTypeName: asset.AssetTypeName,
        ProfilePicture: asset.ProfilePicture,
        PriceUnit: asset.PriceUnit,
        UnitPriceText: asset.UnitPriceText,
        Units: asset.Units,
        Cost: asset.Cost,
        GST: asset.GST,
        TransportPrice: asset.TransportPrice,
        TransportGST: asset.TransportGST,
        startDate: asset.startDate,
        endDate: asset.endDate,
      })),
      totals: {
        subtotal: assets.reduce((acc, asset) => acc + (asset.Cost || 0), 0),
        gst: assets.reduce((acc, asset) => acc + (asset.GST || 0), 0),
        transportTotal: assets.reduce(
          (acc, asset) => acc + (asset.TransportPrice || 0),
          0
        ),
        transportGST: assets.reduce(
          (acc, asset) => acc + (asset.TransportGST || 0),
          0
        ),
        total: assets.reduce(
          (acc, asset) =>
            acc +
            (asset.Cost || 0) +
            (asset.GST || 0) +
            (asset.TransportPrice || 0) +
            (asset.TransportGST || 0),
          0
        ),
      },
      calculatedAt: new Date().toISOString(),
    };
  }; // Function to handle asset selection  // Update submitStepData to include selected assets
  const submitStepData = async (stepData) => {
    setIsStepSubmitting(true);

    try {
      const calculator = new PropertyPriceCalculator(properties);
      const priceSummary = createCleanPriceSummary(
        calculator,
        quotationTemplate
      );
      const orderSummary = createOrderSummary(selectedAssets);

      // Create a copy of the wizard steps and update the ShopStep to include selected assets
      const updatedWizardSteps = [...quotationTemplate.wizardSteps];
      const shopStepIndex = updatedWizardSteps.findIndex(
        (step) => step.id === "ShopStep"
      );

      if (shopStepIndex !== -1) {
        // Update the ShopStep with selected assets
        updatedWizardSteps[shopStepIndex] = {
          ...updatedWizardSteps[shopStepIndex],
          value:
            selectedAssets.length > 0
              ? `${selectedAssets.length} items selected`
              : "",
          selectedAssets: selectedAssets, // Store selected assets in the ShopStep
          // Create fields for each selected asset
          fields: selectedAssets.map((asset, index) => ({
            id: `asset_${asset.Id}`,
            label: asset.Name,
            dataType: "asset",
            isRequired: false,
            value: asset.Name,
            assetData: asset, // Store the full asset data
          })),
        };
      }
      const contentObj = {
        ...quotationTemplate,
        ...(company.ehqLevel === "Pricing" && { priceSummary }),
        orderSummary,
        step: currentStepId,
        wizardSteps: updatedWizardSteps, // Use updated wizard steps with assets
        // Only include selectedAssets in ShopStep, not as a top-level property
      };

      const stepRequest = {
        ...request,
        content: JSON.stringify(contentObj),
        Step: currentStepId,
      };

      const response = await KruozAPI.post(
        "/Quotation/QuotationRequest",
        stepRequest
      );

      if (response.status === 200) {
        setRequest((prev) => ({
          ...prev,
          id: response.data,
          content: JSON.stringify(contentObj),
        }));

        setStepSubmissionStatus((prev) => ({
          ...prev,
          [currentStepId]: true,
        }));

        return true;
      }
      throw new Error("Step submission failed");
    } catch (error) {
      console.error("Error submitting step data:", error);
      return false;
    } finally {
      setIsStepSubmitting(false);
    }
  };
  // Function to handle asset selection
  const handleAssetSelect = useCallback(
    (asset) => {
      const updatedAssets = [...selectedAssets, asset];
      setSelectedAssets(updatedAssets);

      // Simply update the local state without saving to the server when adding items
      // This prevents adding asset items to content JSON on every selection
      // Assets will only be saved when moving to the next step or on final submission
    },
    [selectedAssets]
  );
  const handleOrderChange = useCallback(
    (updatedAssets) => {
      setSelectedAssets(updatedAssets);

      // Update the ShopStep in the quotationTemplate locally without saving to server
      setQuotationTemplate((prevTemplate) => {
        const updatedTemplate = { ...prevTemplate };
        const shopStepIndex = updatedTemplate.wizardSteps.findIndex(
          (step) => step.id === "ShopStep"
        );

        if (shopStepIndex !== -1) {
          updatedTemplate.wizardSteps[shopStepIndex] = {
            ...updatedTemplate.wizardSteps[shopStepIndex],
            value:
              updatedAssets.length > 0
                ? `${updatedAssets.length} items selected`
                : "",
            selectedAssets: updatedAssets,
            fields: updatedAssets.map((asset, index) => ({
              id: `asset_${asset.Id}`,
              label: asset.Name,
              dataType: "asset",
              isRequired: false,
              value: asset.Name,
              assetData: asset,
            })),
          };
        }

        return updatedTemplate;
      });
      // We don't save to the server when assets are changed
      // Assets will only be saved when moving to the next step or on final submission
    },
    [selectedAssets]
  );

  const handleInputChange = useCallback(
    (fieldId, value) => {
      if (fieldId === "customerUserId") {
        setRequest({ ...request, customerUserId: value });
      }

      // Special case for ShopStep assets
      if (fieldId === "shopStepAssets" && Array.isArray(value)) {
        setSelectedAssets(value);
        return;
      }

      setQuotationTemplate((prevTemplate) => {
        const updatedTemplate = { ...prevTemplate };
        let isFieldUpdated = false;
        const hireDurationStep = updatedTemplate.wizardSteps.find(
          (step) => step.id === "hireDuration"
        );
        if (hireDurationStep) {
          const field = hireDurationStep.fields.find(
            (field) => field.id === fieldId
          );
          if (field) {
            field.value = value;
            isFieldUpdated = true;

            const deliveryDateField = hireDurationStep.fields.find(
              (field) => field.id === "deliveryDate"
            );
            const rentalDurationField = hireDurationStep.fields.find(
              (field) => field.id === "rentalDuration"
            );
            const collectionDateField = hireDurationStep.fields.find(
              (field) => field.id === "collectionDate"
            );
            const rentalLength = hireDurationStep.fields.find(
              (field) => field.id === "rentalLength"
            );

            // Recalculate collection date when related fields change.
            // rentalDurationField.value holds the unit (Days | Weeks | Months)
            // rentalLength.value holds the numeric quantity (1,2,3,...)
            if (
              deliveryDateField?.value &&
              rentalDurationField?.value &&
              collectionDateField
            ) {
              const lengthVal = parseInt(rentalDurationField?.value, 10);
              if (!isNaN(lengthVal) && lengthVal > 0) {
                const unit = rentalDurationField.value;
                if (unit === "Months") {
                  // For months we adjust the month component directly (avoids ambiguous day counts)
                  const date = new Date(deliveryDateField.value);
                  date.setMonth(date.getMonth() + lengthVal);
                  collectionDateField.value = date.toISOString().split("T")[0];
                } else {
                  // Convert to total rental days (Days => n, Weeks => n * 7)
                  let rentalDays = lengthVal;
                  if (unit === "Weeks") rentalDays = lengthVal * 7;
                  // Pass rental days to existing helper
                  const newCollectionDate = calculateCollectionDate(
                    deliveryDateField.value,
                    rentalDays
                  );
                  if (newCollectionDate) {
                    collectionDateField.value = newCollectionDate;
                  }
                }
              }
            }
          }
        }

        updatedTemplate.wizardSteps.forEach((step) => {
          step.fields.forEach((field) => {
            if (field.id === fieldId) {
              field.value = value;
              isFieldUpdated = true;
              if (
                field.considerInAssetSelection &&
                field.considerInAssetSelection
              ) {
                fetchAvailableAssets();
              }
            }
          });
        });

        return isFieldUpdated ? updatedTemplate : prevTemplate;
      });

      setErrors((prevErrors) => ({ ...prevErrors, [fieldId]: "" }));
      setShowErrorSummary(false);
    },
    [request]
  );
  const validateStep = useCallback(() => {
    if (!quotationTemplate || !quotationTemplate.wizardSteps) {
      console.error("Invalid quotation template structure");
      return false;
    }

    const currentStepData = quotationTemplate.wizardSteps.find(
      (step) => step.id === currentStepId
    );

    if (!currentStepData || !Array.isArray(currentStepData.fields)) {
      console.error(`Invalid step data for step ${currentStepId}`);
      return false;
    }

    const newErrors = {};
    // Special validation for ShopStep: if there are available assets but none selected, show error
    if (
      currentStepId === "ShopStep" &&
      availableAssets.length > 0 &&
      selectedAssets.length === 0
    ) {
      newErrors["assetSelection"] =
        "Please select at least one item to continue";
    } else {
      // Standard field validation for other steps
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      currentStepData.fields.forEach((field) => {
        // Required empty
        if (field.isRequired && !field.value) {
          newErrors[field.id] = `${field.label} is required`;
          return; // skip further checks for this field
        }
        // Email format (if value present OR required & not empty)
        if (field.dataType === "Email" && field.value) {
          if (!emailRegex.test(String(field.value).trim())) {
            newErrors[field.id] = "Please enter a valid email address";
          }
        }
      });
    }

    setErrors(newErrors);
    setShowErrorSummary(Object.keys(newErrors).length > 0);
    return Object.keys(newErrors).length === 0;
  }, [quotationTemplate, currentStepId, availableAssets, selectedAssets]);
  const handleNext = useCallback(async () => {
    if (validateStep()) {
      const currentIndex = stepOrder.indexOf(currentStepId);
      const nextStepId = stepOrder[currentIndex + 1];
      const currentStepData = quotationTemplate.wizardSteps.find(
        (step) => step.id === currentStepId
      );

      // Only include selected assets if this is the ShopStep
      const stepDataWithAssets =
        currentStepId === "ShopStep"
          ? {
              ...currentStepData,
              selectedAssets: selectedAssets,
            }
          : currentStepData;

      const success = await submitStepData(stepDataWithAssets);
      if (success) {
        setCurrentStepId(nextStepId);
      }
    }
  }, [
    validateStep,
    quotationTemplate,
    currentStepId,
    stepOrder,
    selectedAssets,
    submitStepData,
  ]);

  const handlePrevious = useCallback(() => {
    const currentIndex = stepOrder.indexOf(currentStepId);
    const previousStepId = stepOrder[currentIndex - 1];
    setCurrentStepId(previousStepId);
    setShowErrorSummary(false);
  }, [currentStepId, stepOrder]);

  const createCleanPriceSummary = (calculator, template) => {
    const prices = calculator.calculateAllPrices(template);

    return {
      breakdown: prices.map((price) => ({
        fieldId: price.fieldId,
        fieldLabel: price.fieldLabel,
        total: price.total,
        details: price.details.map((detail) => ({
          optionLabel: detail.optionLabel,
          propertyId: detail.propertyId,
          propertyLabel: detail.propertyLabel,
          price: detail.price,
        })),
      })),
      totalPrice: prices.reduce((sum, item) => sum + item.total, 0),
      currency: "AUD",
      calculatedAt: new Date().toISOString(),
    };
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Enforce Terms & Conditions acceptance on final submit
      if (currentStepId === "preview" && !termsAccepted) {
        toast.error("Please accept the Terms & Conditions to submit.");
        return;
      }

      // Check if wizard has ShopStep and selected assets
      const hasShopStep = quotationTemplate.wizardSteps.some(
        (step) => step.id === "ShopStep"
      );
      const hasSelectedAssets = selectedAssets && selectedAssets.length > 0;

      // Gather contact info and other data
      const contactInfoStep = quotationTemplate.wizardSteps.find(
        (step) => step.id === "ContactInfo"
      );
      let customerEmail = "";
      let customerName = "";
      let customerFirstName = "";
      let customerLastName = "";
      let mobile = "";
      let businessName = "";
      let identityNumber = "";
      let telephoneWork = "";
      let telephoneHome = "";
      let secondContactEmail = "";
      let secondContactName = "";
      let secondContactTelephone = "";
      let notes = "";

      if (contactInfoStep) {
        const emailField = contactInfoStep.fields.find(
          (field) => field.id === "Email"
        );
        if (emailField) {
          if (!emailField.value) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              [emailField.id]: `${emailField.label} is required`,
            }));
            setShowErrorSummary(true);
            return;
          }
          customerEmail = emailField.value;
        }
        debugger;
        // Get other contact fields
        customerFirstName =
          contactInfoStep.fields.find((field) => field.id === "FirstName")
            ?.value || "";
        customerLastName =
          contactInfoStep.fields.find((field) => field.id === "LastName")
            ?.value || "";
        if (customerFirstName == "") {
          customerFirstName =
            contactInfoStep.fields.find((field) => field.id === "FullName")
              ?.value || "";
        }
        customerName = `${customerFirstName} ${customerLastName}`.trim();
        mobile =
          contactInfoStep.fields.find((field) => field.id === "MobileNumber")
            ?.value || "";
        businessName =
          contactInfoStep.fields.find((field) => field.id === "BusinessName")
            ?.value || "";
        identityNumber =
          contactInfoStep.fields.find((field) => field.id === "IdentityNumber")
            ?.value || "";
        telephoneWork =
          contactInfoStep.fields.find((field) => field.id === "WorkNumber")
            ?.value || "";
        telephoneHome =
          contactInfoStep.fields.find((field) => field.id === "HomeNumber")
            ?.value || "";
        notes =
          contactInfoStep.fields.find((field) => field.id === "Notes")?.value ||
          "";
        secondContactEmail =
          contactInfoStep.fields.find(
            (field) => field.id === "SecondContactEmail"
          )?.value || "";
        secondContactName =
          contactInfoStep.fields.find(
            (field) => field.id === "SecondContactName"
          )?.value || "";
        secondContactTelephone =
          contactInfoStep.fields.find(
            (field) => field.id === "SecondContactMobile"
          )?.value || "";
      }

      // Only gather location and duration data if there's a shop step      // Create a cloned template with selected assets ONLY in ShopStep, not at root level
      const templateWithProperAssetPlacement = {
        ...quotationTemplate,
        wizardSteps: quotationTemplate.wizardSteps.map((step) => {
          if (step.id === "ShopStep" && hasSelectedAssets) {
            return {
              ...step,
              selectedAssets: selectedAssets,
              value:
                selectedAssets.length > 0
                  ? `${selectedAssets.length} items selected`
                  : "",
              fields: selectedAssets.map((asset) => ({
                id: `asset_${asset.Id}`,
                label: asset.Name,
                dataType: "asset",
                isRequired: false,
                value: asset.Name,
                assetData: asset,
              })),
            };
          }
          return step;
        }),
      };

      let submissionData = {
        quotationData: {
          customerEmail,
          customerName,
          templateData: {
            ...templateWithProperAssetPlacement,
            ...(company.ehqLevel === "Pricing" && {
              priceSummary: createCleanPriceSummary(
                new PropertyPriceCalculator(properties),
                templateWithProperAssetPlacement
              ),
            }),
            orderSummary: hasSelectedAssets
              ? createOrderSummary(selectedAssets)
              : null,
          },
        },
      };

      // Only prepare order data if shop step exists and has selected assets
      if (hasShopStep) {
        // && hasSelectedAssets
        // Prefer addresses from ContactInfo step if present; fallback to location step
        const locationStep = quotationTemplate.wizardSteps.find(
          (step) => step.id === "location"
        );
        // Build shipping (delivery) and billing addresses from ContactInfo
        let shippingAddress = "";
        let billingAddress = "";
        if (contactInfoStep) {
          const deliveryAddress1 =
            contactInfoStep.fields.find((f) => f.id === "DeliveryAddress1")
              ?.value || "";
          const deliveryAddress2 =
            contactInfoStep.fields.find((f) => f.id === "DeliveryAddress2")
              ?.value || "";
          const billingAddress1 =
            contactInfoStep.fields.find((f) => f.id === "BillingAddress1")
              ?.value || "";
          const billingAddress2 =
            contactInfoStep.fields.find((f) => f.id === "BillingAddress2")
              ?.value || "";

          shippingAddress = [deliveryAddress1, deliveryAddress2]
            .filter(Boolean)
            .join(" ");
          billingAddress = [billingAddress1, billingAddress2]
            .filter(Boolean)
            .join(" ");
        }
        // Fallback to location step if ContactInfo addresses are not available
        if (!shippingAddress) {
          shippingAddress =
            locationStep?.fields.find(
              (field) => field.id === "deliveryLocation"
            )?.value || "";
        }
        if (!billingAddress) {
          billingAddress =
            locationStep?.fields.find((field) => field.id === "billingLocation")
              ?.value ||
            shippingAddress ||
            "";
        }
        const shippingAddressLat =
          locationStep?.fields.find((field) => field.id === "Latitude")
            ?.value || 0;
        const shippingAddressLng =
          locationStep?.fields.find((field) => field.id === "Longitude")
            ?.value || 0;

        const transportDistance =
          locationStep?.fields.find((field) => field.id === "Distance")
            ?.value || 0;

        // Get dates from hire duration step
        const hireDurationStep = quotationTemplate.wizardSteps.find(
          (step) => step.id === "hireDuration"
        );
        const startDate = hireDurationStep?.fields.find(
          (field) => field.id === "deliveryDate"
        )?.value;
        const endDate = hireDurationStep?.fields.find(
          (field) => field.id === "collectionDate"
        )?.value;
        debugger;
        // Additional validation for order-specific fields
        if (!shippingAddress || !mobile || !transportDistance) {
          setShowErrorSummary(true);
          toast.error("Please fill in all required fields for order creation");
          return;
        }

        submissionData.orderData = {
          customerEmail,
          customerFirstName,
          customerLastName,
          businessName,
          identityNumber,
          shippingAddress,
          shippingAddressLat,
          shippingAddressLng,
          contactNo1: mobile,
          companyId: company.id,
          requestId: null,
          timeZone: company.timeZone,
          billingAddress,
          telephoneWork,
          telephoneHome,
          secondContactEmail,
          secondContactName,
          secondContactTelephone,
          transportDistance,
          functionType: notes,
          selectedAssets: selectedAssets.map((asset) => ({
            ...asset,
            StartDate: new Date(startDate).toISOString(),
            EndDate: new Date(endDate).toISOString(),
          })),
          customerId: user?.id || "None",
          orderNumber: "",
          userId: user?.id || "",
          showPrices: true,
          OrderNumber: "123",
          requestId: request.id,
          orderDate: new Date().toISOString(),
          createdBy: sessionStorage.getItem("adminPortalUid") || "Customer",
        };
      }

      // Store submission data temporarily
      setRequest((prev) => ({
        ...prev,
        submissionData,
      }));

      // Show confirmation dialog
      setShowConfirmDialog(true);
    },
    [quotationTemplate, properties, selectedAssets, company, user]
  );
  const submitQuotationRequest = async () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);

    try {
      const { submissionData } = request;
      debugger;
      // Only submit order if we have order data
      if (company.ehqLevel == "Asset Selection" && submissionData.orderData) {
        const orderResponse = await KruozAPI.post(
          "/Order/Create",
          submissionData.orderData
        );

        if (!orderResponse.data.isSuccess) {
          throw new Error(
            orderResponse.data.message || "Order creation failed"
          );
        }

        // Update quotation request with order details
        submissionData.quotationData.orderId = orderResponse.data.id;
        submissionData.quotationData.orderNumber =
          orderResponse.data.orderNumber;
      }

      // Submit quotation
      const quotationRequest = {
        ...request,
        customerEmail: submissionData.quotationData.customerEmail,
        customerName: submissionData.quotationData.customerName,
        content: JSON.stringify(submissionData.quotationData.templateData),
        status: "Submitted",
        step: currentStepId,
        ...(company.ehqLevel == "Asset Selection" &&
          submissionData.orderData && {
            orderId: submissionData.quotationData.orderId,
            orderNumber: submissionData.quotationData.orderNumber,
          }),
      };

      const quotationResponse = await KruozAPI.post(
        "/Quotation/QuotationRequest",
        quotationRequest
      );
      if (quotationResponse.status === 200) {
        const submittedRequestId = quotationResponse.data;
        // Navigate directly to the submitted request view instead of showing success message
        if (submissionData.quotationData.orderNumber) {
          navigate(`/order/${submissionData.quotationData.orderNumber}`);
        } else {
          navigate(`/request/${submittedRequestId}`);
        }
        return; // Return early to avoid showing the success message
      } else {
        throw new Error("Quote submission failed");
      }
    } catch (error) {
      console.error("Error in submission:", error);
      toast.error(error.message || "Failed to submit. Please try again.");
      setIsSubmitting(false); // Only reset isSubmitting on error
    }
  };
  const handleCreateNew = useCallback(() => {
    setIsSubmitting(false);
    // No longer need to set isSubmitSuccess since we redirect immediately
    setCurrentStepId(quotationTemplate.wizardSteps[0]?.id || "");
    setSelectedAssets([]);

    const freshTemplate = { ...company.quotationTemplate };
    const currentContactStep = quotationTemplate.wizardSteps.find(
      (step) => step.id === "ContactInfo"
    );

    // Reset the ShopStep to its initial state
    const shopStepIndex = freshTemplate.wizardSteps.findIndex(
      (step) => step.id === "ShopStep"
    );
    if (shopStepIndex !== -1) {
      freshTemplate.wizardSteps[shopStepIndex] = {
        ...freshTemplate.wizardSteps[shopStepIndex],
        value: "",
        selectedAssets: [],
        fields: [],
      };
    }

    freshTemplate.wizardSteps = freshTemplate.wizardSteps.map((step) => {
      if (step.id === "ContactInfo" && currentContactStep) {
        return {
          ...step,
          fields: step.fields.map((freshField) => {
            const currentField = currentContactStep.fields.find(
              (field) => field.id === freshField.id
            );
            return currentField
              ? { ...freshField, value: currentField.value }
              : { ...freshField, value: "" };
          }),
        };
      }

      return {
        ...step,
        fields: step.fields.map((field) => ({
          ...field,
          value: "",
        })),
      };
    });

    setQuotationTemplate(freshTemplate);
  }, [company.quotationTemplate, quotationTemplate]);

  const formatNumber = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const calculateTotals = useCallback(() => {
    if (!selectedAssets || selectedAssets.length === 0) {
      return {
        subtotal: 0,
        vat: 0,
        transportTotal: 0,
        transportGST: 0,
        total: 0,
      };
    }

    const subtotal = selectedAssets.reduce(
      (acc, asset) => acc + (asset.Cost || 0),
      0
    );
    const vat = selectedAssets.reduce(
      (acc, asset) => acc + (asset.GST || 0),
      0
    );
    const transportTotal = selectedAssets.reduce(
      (acc, asset) => acc + (asset.TransportPrice || 0),
      0
    );
    const transportGST = selectedAssets.reduce(
      (acc, asset) => acc + (asset.TransportGST || 0),
      0
    );
    const total = subtotal + vat + transportTotal + transportGST;

    return { subtotal, vat, transportTotal, transportGST, total };
  }, [selectedAssets]);

  const ConfirmationDialog = () => {
    const hasOrderData = request.submissionData?.orderData;
    const assetCount = selectedAssets?.length || 0;
    const totals = calculateTotals();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">Confirm Submission</h2>
          <div className="space-y-4">
            {/* <p className="text-gray-600">Please review your request details:</p> */}
            {hasOrderData && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-semibold text-gray-700 mb-2">
                  Order Summary:
                </h3>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>Selected Items: {assetCount}</li>
                  <li>Total Amount: ${formatNumber(totals.total)}</li>
                </ul>
              </div>
            )}
            <p className="text-gray-600">
              Are you sure you want to submit this{" "}
              {hasOrderData ? "order " : ""}?
            </p>
          </div>
          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={() => setShowConfirmDialog(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submitQuotationRequest}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              Confirm & Submit
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Make sure the background image is included in the quotation template
    const updatedTemplate = {
      ...company.quotationTemplate,
      backgroundImage:
        company.backgroundImage || company.quotationTemplate?.backgroundImage,
    };

    setQuotationTemplate(updatedTemplate);
  }, [company.quotationTemplate, company.backgroundImage]);

  const handleNavigateToCustomerHome = () => {
    setShowNavigationConfirm(true);
  };

  const confirmNavigation = () => {
    setShowNavigationConfirm(false);
    navigate("/customer-home");
  };

  const cancelNavigation = () => {
    setShowNavigationConfirm(false);
  };

  const NavigationConfirmDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Confirm Navigation</h2>
          <button
            onClick={cancelNavigation}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes size={24} />
          </button>
        </div>
        <p className="mb-6">
          Are you sure you want to leave this page? Your changes will be
          discarded.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={cancelNavigation}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmNavigation}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  // Fetch availability as soon as dates are available and whenever they change
  useEffect(() => {
    const hireDurationStep = quotationTemplate?.wizardSteps?.find(
      (s) => s.id === "hireDuration"
    );
    const deliveryDate = hireDurationStep?.fields?.find(
      (f) => f.id === "deliveryDate"
    )?.value;
    const collectionDate = hireDurationStep?.fields?.find(
      (f) => f.id === "collectionDate"
    )?.value;
    if (deliveryDate && collectionDate) {
      fetchAvailableAssets();
    }
  }, [quotationTemplate]);

  // Calculate totals for display in the header bar
  const totals = calculateTotals();
  // Read delivery/collection dates for availability header logic
  const _hireStep = quotationTemplate?.wizardSteps?.find(
    (s) => s.id === "hireDuration"
  );
  const _deliveryDate = _hireStep?.fields?.find(
    (f) => f.id === "deliveryDate"
  )?.value;
  const _collectionDate = _hireStep?.fields?.find(
    (f) => f.id === "collectionDate"
  )?.value;

  if (!quotationTemplate || !Array.isArray(quotationTemplate.wizardSteps)) {
    return (
      <div className="max-w-6xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6"
          role="alert"
        >
          <p className="font-bold">Error</p>
          <p>
            Invalid configuration. Please check your wizard setup and try again.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div
      className="min-h-screen bg-gray-100"
      style={{
        backgroundImage: company.backgroundImage
          ? `url(${
              process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL +
              company.backgroundImage
            })`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Toaster position="top-right" />
      <img
        src={
          process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL + company.backgroundImage
        }
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover z-0"
      />
      {/* Main content */}
      <main className="max-w-6xl mx-auto mt-8 p-6 bg-white bg-opacity-95 rounded-lg shadow-xl relative z-10">
        {isStepSubmitting || isSubmitting ? (
          <SubmissionStatus
            isSubmitting={isSubmitting}
            isStepSubmitting={isStepSubmitting}
            isSuccess={false}
            onNavigateHome={() => navigate(`/request/${request.id}`)}
            onCreateNew={handleCreateNew}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-blue-600">
                Place an Order or Get a Free Quote
              </h2>
              <div className="flex items-center space-x-4">
                <img
                  src={
                    process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL + company.logo
                  }
                  alt={company.name}
                  className="h-12 w-auto"
                />
                {/* <h1 className="text-2xl font-bold text-gray-800">{company.name}</h1> */}
              </div>
            </div>

            <StepIndicator
              steps={[
                ...quotationTemplate.wizardSteps,
                { id: "preview", label: "Preview & Submit" },
              ]}
              currentStep={currentStepIndex}
            />
            {/* Availability header */}
            <div className="mb-6">
              <div className="border-b pb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="w-full sm:w-auto">
                  {currentStepId === "ShopStep" && (
                    <>
                      <h2 className="text-xl font-semibold text-gray-800">
                        Recommended
                      </h2>
                      <p className="text-sm text-gray-500">
                        Based on your requirements
                      </p>
                    </>
                  )}
                </div>
                <div className="w-full sm:w-auto text-left sm:text-right">
                  {selectedAssets.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setShowOrderSummaryModal(true)}
                      className="w-full sm:w-auto flex flex-wrap items-center justify-between sm:justify-center px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 hover:border-green-300 transition"
                      title="View order summary"
                    >
                      {_deliveryDate &&
                        _collectionDate &&
                        availableAssets?.length > 0 && (
                          <span className="ml-1 text-green-600">
                            • {availableAssets.length} items available for
                            booking&nbsp;
                          </span>
                        )}
                      <span className="font-medium">
                        • {selectedAssets.length} items selected
                      </span>
                      <span className="ml-2 font-semibold text-green-800">
                        • Total: ${formatNumber(totals.total)}
                      </span>
                    </button>
                  ) : (
                    <p className="text-lg font-medium text-green-600 text-left sm:text-right">
                      {!_deliveryDate || !_collectionDate ? (
                        <span className="text-sm text-gray-500">
                          Set dates to see availability
                        </span>
                      ) : isCheckingAvailability ? (
                        <span className="text-sm text-gray-500">
                          Checking availability…
                        </span>
                      ) : hasAttemptedFetch &&
                        (availableAssets?.length ?? 0) === 0 ? (
                        <span className="text-sm text-gray-500">
                          No items currently available for booking
                        </span>
                      ) : typeof availableAssets?.length === "number" &&
                        availableAssets.length > 0 ? (
                        <>
                          <span>0 items selected</span>
                          <span className="ml-1">
                            • {availableAssets.length} items available for
                            booking
                          </span>
                        </>
                      ) : null}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {showErrorSummary && (
              <div
                className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6"
                role="alert"
              >
                <p className="font-bold">
                  Please correct the following errors:
                </p>
                <ul className="list-disc list-inside">
                  {Object.entries(errors).map(([fieldId, errorMessage]) => (
                    <li key={fieldId}>{errorMessage}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col">
              <div className="w-full mb-6">
                <form onSubmit={handleSubmit}>
                  {currentStepId === "preview" ? (
                    <PreviewStep
                      quotationTemplate={quotationTemplate}
                      properties={properties}
                      companyId={company.id}
                      onOrderChange={handleOrderChange}
                      selectedAssets={selectedAssets}
                      setSelectedAssets={setSelectedAssets}
                      termsUrl={
                        "https://app.kruoz.com/company/terms/" + company.id
                      }
                      termsAccepted={termsAccepted}
                      onToggleTerms={setTermsAccepted}
                    />
                  ) : currentStepId === "ContactInfo" ? (
                    <ContactInfoStep
                      step={quotationTemplate.wizardSteps.find(
                        (step) => step.id === currentStepId
                      )}
                      handleInputChange={handleInputChange}
                      errors={errors}
                      user={user}
                      login={login}
                    />
                  ) : currentStepId === "ShopStep" ? (
                    <ShopStep
                      step={quotationTemplate.wizardSteps.find(
                        (step) => step.id === currentStepId
                      )}
                      quotationTemplate={quotationTemplate}
                      companyId={company.id}
                      onAssetSelect={handleAssetSelect}
                      selectedAssets={selectedAssets}
                      onAvailableAssetsChange={setAvailableAssets}
                      errors={errors}
                    />
                  ) : (
                    <WizardStep
                      company={company}
                      step={quotationTemplate.wizardSteps.find(
                        (step) => step.id === currentStepId
                      )}
                      handleInputChange={handleInputChange}
                      errors={errors}
                    />
                  )}
                  <NavigationButtons
                    currentStep={currentStepIndex}
                    totalSteps={stepOrder.length}
                    handlePrevious={handlePrevious}
                    handleNext={handleNext}
                    isSubmitting={isSubmitting}
                    isSubmitDisabled={
                      currentStepId === "preview" && !termsAccepted
                    }
                  />
                </form>
              </div>
            </div>
          </>
        )}
      </main>

      {showConfirmDialog && <ConfirmationDialog />}
      {showNavigationConfirm && <NavigationConfirmDialog />}
      {showOrderSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl mx-4 rounded-lg shadow-xl p-4 relative">
            <button
              type="button"
              onClick={() => setShowOrderSummaryModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Close order summary"
            >
              <FaTimes size={20} />
            </button>
            <h3 className="text-lg font-semibold mb-3">Order Summary</h3>
            <OrderSummery
              quotationTemplate={quotationTemplate}
              properties={properties}
              companyId={company.id}
              onOrderChange={handleOrderChange}
              selectedAssets={selectedAssets}
              setSelectedAssets={setSelectedAssets}
              setShowOrderSummaryModal={setShowOrderSummaryModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestWizard;
